if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          optionSelect: "[data-sticky-option-select]",
        };
        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
        this.handleOrderDiscountChange = () => this.updateOptionPrices();
      }

      connectedCallback() {
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm ? this.mainProductForm.querySelector(".m-add-to-cart") : null;
        this.mainDynamicCheckout = this.mainProductForm.querySelector(this.selectors.buyNowBtn);
        this.disableSelectedVariantDefault = this.mainProduct.dataset.disableSelectedVariantDefault === "true" || false;
        this.domNodes = queryDomNodes(this.selectors, this.container);

        this.init();
      }

      disconnectedCallback() {
        document.removeEventListener("nelpanda:order-discount", this.handleOrderDiscountChange);
      }

      init() {
        if (!this.mainAddToCart) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        this.variantData = this.getVariantData();

        const headerHeight = MinimogSettings.headerHeight || 66;
        const rootMargin = `${headerHeight}px 0px 0px 0px`;

        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio !== 1) {
                  this.container.style.setProperty("--m-translate-y", 0);
                } else {
                  this.container.style.setProperty("--m-translate-y", "100%");
                }
                document.documentElement.classList[entry.intersectionRatio !== 1 ? "add" : "remove"]("stick-atc-show");
              });
            },
            { threshold: 1, rootMargin }
          );
        }

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        const { prodTitle, mainImage } = this.domNodes;
        prodTitle.addEventListener("click", () => __scrollToTop());
        mainImage.addEventListener("click", () => __scrollToTop());

        this.handleCustomFields();

        this.setObserveTarget();
        this.syncWithMainProductForm();
        this.updateOptionPrices();
        document.addEventListener("nelpanda:order-discount", this.handleOrderDiscountChange);

        const mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        mql.onchange = this.setStickyAddToCartHeight.bind(this);
        this.setStickyAddToCartHeight();

        this.querySelectorAll(this.selectors.optionSelect).forEach((select) => {
          select.addEventListener("change", () => this.handleOptionChange());
        });
      }

      handleOptionChange() {
        const variantPicker = this.mainProduct.querySelector('variant-picker');
        this.currentVariant = this.findVariant(this.getSelectedOptions());
        const variantInput = this.querySelector(this.selectors.variantIdSelect);

        variantInput.value = this.currentVariant ? this.currentVariant.id : '';
        this.currentVariant
          ? this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut)
          : this.toggleAddButton(true, window.MinimogStrings.unavailable);

        this.updateOptionPrices();

        if (!variantPicker) return;
        this.querySelectorAll(this.selectors.optionSelect).forEach((select) => {
          const selectedOption = select.options[select.selectedIndex];
          const optionId = selectedOption?.dataset.optionValueId;
          if (!optionId) return;

          const input = variantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
          if (!input) return;

          if (input.tagName === "OPTION") {
            input.parentNode.value = input.value;
          } else if (input.tagName === "INPUT") {
            input.checked = true;
          }
        });
        variantPicker.dispatchEvent(new Event("change", { bubbles: true }));
      }

      getSelectedOptions() {
        const selects = this.querySelectorAll(this.selectors.optionSelect);
        const selectedOptions = Array(selects.length);
        selects.forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          selectedOptions[position] = select.value;
        });
        return selectedOptions;
      }

      findVariant(selectedOptions) {
        return this.variantData.find((variant) => (
          variant.options.every((option, index) => option === selectedOptions[index])
        ));
      }

      findVariantForOption(position, value) {
        const selectedOptions = this.getSelectedOptions();
        selectedOptions[position] = value;
        return this.findVariant(selectedOptions);
      }

      getDisplayPrice(variant) {
        if (!variant) return null;
        const productPrice = window.NelpandaFinalPrice?.projectVariant(variant, undefined, this.dataset.productHandle).price ?? variant.price;
        const eligible = window.NelpandaFinalPrice?.variantDiscountEligible(variant, this.dataset.productHandle, this.dataset.productDiscountEligible !== 'false') ?? (this.dataset.productDiscountEligible !== 'false');
        return window.NelpandaFinalPrice?.applyOrderDiscount(productPrice, undefined, eligible) ?? productPrice;
      }

      updateOptionPrices() {
        this.querySelectorAll(`${this.selectors.optionSelect}[data-show-price="true"]`).forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          Array.from(select.options).forEach((option) => {
            const label = option.dataset.optionLabel || option.value;
            const variant = this.findVariantForOption(position, option.value);
            const price = this.getDisplayPrice(variant);
            option.textContent = price == null || typeof window.formatMoney !== "function"
              ? label
              : `${label} - ${window.formatMoney(price, window.MinimogSettings.money_format)}`;
          });
        });
      }

      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      setObserveTarget() {
        if (this.observer) {
          this.observer.observe(this.mainProductForm);
          this.observeTarget = this.mainProductForm;
        }
      }

      setUnavailable() {
        const productForm = this.querySelector('.m-product-form');
        const addButton = productForm.querySelector('[name="add"]');

        if (addButton) {
          this.toggleAddButton(true, window.MinimogStrings.unavailable);
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.m-product-form');
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      handleDisableSelectedVariantDefault() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        this.querySelectorAll(this.selectors.optionSelect).forEach((select) => {
          select.selectedIndex = -1;
        });
        this.querySelector(this.selectors.variantIdSelect).value = '';
        this.setUnavailable();
      }

      setStickyAddToCartHeight() {
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", this.offsetHeight + "px");
        window.MinimogSettings.stickyAddToCartHeight = this.offsetHeight;
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          const isMainProduct = e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;
          this.currentVariant = e.data.variant;
          if (this.currentVariant) {
            variantInput.value = e.data.variant.id;
            this.querySelectorAll(this.selectors.optionSelect).forEach((select) => {
              const position = Number(select.dataset.optionPosition) - 1;
              select.value = e.data.variant.options[position];
            });
            this.updateOptionPrices();
            this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut)
          } else {
            variantInput.value = '';
            this.toggleAddButton(true, window.MinimogStrings.unavailable)
          }
        });
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn } = this.domNodes;

        let hasCustomFieldRequired = false;
        const customFields = document.querySelectorAll(".m-main-product--info .m-product-custom-field");
        let customFieldFirst = customFields[0];
        customFields &&
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field.value == "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

        hasCustomFieldRequired &&
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = validateForm(this.mainProductInfo);
              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                __scrollToTop(this.mainProductInfo, () => this.mainDynamicCheckout.click());
              }
            },
            true
          );
        }
      }
    }
  );
}
